import { format } from 'd3-format';
import { startCase } from 'lodash';
import React from 'react';
import './Sidebar.css';
import Slideshow from './Slideshow';
import './UrbanizeSidebar.css';

const getStatusColor = status => {
  switch (status) {
    case 'proposed':
      return 'rgb(80,175,222)';
    case 'approved':
      return 'rgb(76,156,142)';
    case 'construction':
      return 'rgb(221,202,88)';
    case 'under_construction':
      return 'rgb(221,202,88)';
    case 'completed':
      return 'rgb(183,97,166)';
    case 'cancelled':
      return 'rgb(207,214,211)';
    default:
      return '#fff';
  }
};

const Row = ({ label, value }) => {
  if (!value) return null;
  return (
    <div className="row">
      <div className="label">{label}</div>
      <div className="value">{value}</div>
    </div>
  );
};

const fmt = format(',.0f');
function floatFormat(num) {
  if (!num) return null;
  try {
    const numnum = fmt(+num);
    return numnum;
  } catch (e) {
    return num;
  }
}

function splitFormat(v, upperFirst = false) {
  if (!v) return null;
  return joinWithEl(
    v.split(',').map(val => (upperFirst ? startCase(val) : val)),
    <br key='bri' />
  );
}

function underFormat(v) {
  if (!v) return null;
  return v.replaceAll('_', ' ');
}

function joinWithEl(rows, el) {
  if (rows.length === 0) return null;
  return rows.reduce((prev, curr) => [prev, el, curr]);
}

function getImageUrls(images) {
  if (!images) {
    return [];
  }

  if (typeof images === 'string') {
    // some images are comma separated string
    return images.split(',').filter(l => l.trim() !== '');
  }

  if (Array.isArray(images)) {
    // some images are arrays of objects
    const urls = [];

    for (const image of images) {
      if (typeof image?.image_url === 'string') {
        urls.push(image.image_url);
      }
    }
    return urls;
  }

  return [];
}

let subdomain = '';
const urlParams = new URLSearchParams(window.location.search);
const urlsubdomain = urlParams.get('subdomain');
if (urlsubdomain) {
  subdomain = urlsubdomain + '.';
}

const Sidebar = ({ data, selection, setSelection, setOpen }) => {
  console.log({ data, selection })
  // http://localhost:3000/urbanize
  // https://urbanize.city/node/{urb_project_id}
  // "https://urbanize-d9.ddev.site/sites/default/files/styles/300x300/public/888%20S%20Hope%20by%20Hunter%20Kerhart%203.jpg?itok=dstFvjQg"
  const selectedIds = new Set(selection);
  const ffeatures = data['features'].filter(f => selectedIds.has(f.properties.id));
  const a = ffeatures.length > 0 ? ffeatures[0].properties : {};

  /* Set the width of the side navigation to 0 and the left margin of the page content to 0 */
  const closeNav = () => {
    setSelection([]);
  };

  const getWidth = () => {
    if (selection.length > 0) return '300px';
    return '0';
  };

  // columns: ['name', 'urb_address', 'urb_project_status','urb_developer', 'urb_architect', 'urb_project_uses', 'urb_num_apts', 'urb_num_parking', 'urb_retail_space_sf'],

  const fact_rows = [
    { key: 'urb_uses', value: splitFormat(a.urb_uses, true), label: 'Asset Type' },
    { key: 'urb_num_floors', value: a.urb_num_floors, label: 'Floors' },
    { key: 'urb_num_apts', value: a.urb_num_apts, label: 'Apts' },
    { key: 'urb_num_parking', value: a.urb_num_parking, label: 'Parking' },
    { key: 'urb_retail_space_sf', value: floatFormat(a.urb_retail_space_sf), label: 'Retail SF' },
    { key: 'urb_office_space_sf', value: floatFormat(a.urb_office_space_sf), label: 'Office SF' }
  ].filter(x => x.value);

  const company_rows = [
    { key: 'urb_developer', value: splitFormat(a.urb_developer), label: 'Developer' },
    { key: 'urb_architect', value: splitFormat(a.urb_architect), label: 'Architect' }
  ].filter(x => x.value);

  // const articleURL = a.urb_articleURL;
  const articleURL = `https://${subdomain}urbanize.city/node/${a.urb_project_id}`;

  return (
    <React.Fragment>
      <div id="urbSidenav" className="sidenav" style={{ width: getWidth() }}>
        <div className="closebtn" onClick={closeNav}>
          &times;
        </div>

        {a.urb_images && <Slideshow urls={getImageUrls(a.urb_images).map(url => {
          if (url.startsWith('https://urbanize-d9.ddev.site')) return url.replace('https://urbanize-d9.ddev.site', `https://${subdomain}urbanize.city`);
          if (url.startsWith('http://default/')) return url.replace('http://default', `https://${subdomain}urbanize.city`);
          return url;
        })} setOpen={setOpen} />}

        <div className="sidebarDetails">
          {a.name && <div className="name-title">{a.name}</div>}
          {a.urb_project_status && (
            <div className="status-row">
              <div className="color-circle" style={{ backgroundColor: getStatusColor(a.urb_project_status) }}></div>
              <div>{underFormat(a.urb_project_status)}</div>
            </div>
          )}
          {a.urb_address && <div className="address-title">{a.urb_address}</div>}
          {a.urb_articleURL && (
            <div className="read-more-row">
              <a target="_blank" rel="noopener noreferrer nofollow" href={articleURL}>
                Read More
              </a>
            </div>
          )}

          {fact_rows.length > 0 && <div className="section-title">Facts</div>}
          {joinWithEl(fact_rows.map(x => <Row {...x} />, <div className="hr"></div>))}

          {/* TODO from Giraffe */}
          {/* siteArea */}
          {/* levels */}
          {/* maxHeight */}
          {/* netArea */}
          {/* grossArea */}
          {/* saleableArea */}
          {/* totalResidents */}
          {/* totalDwellings */}

          {company_rows.length > 0 && <div className="section-title">Companies</div>}
          {joinWithEl(company_rows.map(x => <Row {...x} />, <div className="hr"></div>))}

          {/* {data['properties']['columns'].map(k => {
		  			let content = a[k];
		  			if (!content || content === '') return '';
		  			if (String(content).startsWith('http') || String(content).startsWith('www')) {
		  				return <React.Fragment key={k}>
		  					<h6> {k} </h6>
		  					<a target="_blank" href={content}>{content}</a>
		  				</React.Fragment>
		  			}
		  			if (k.toLowerCase() === 'height') {
		  				return <React.Fragment key={k}>
			  				<h6> Height </h6>
			  				<p> {content} meters </p>
		  				</React.Fragment>
		  			}
		  			return <React.Fragment key={k}>
		  				<h6> {k} </h6>
		  				<p> {content} </p>
		  			</React.Fragment>

		  		})} */}
          {a.urb_project_id && <div className="add-company-row">
            <a href={`https://${subdomain}urbanize.city/projects/add-company?project=${a.urb_project_id}`} target="_blank" rel="noopener noreferrer nofollow">
              Add Your Company
            </a>
          </div>}
        </div>
      </div>
    </React.Fragment>
  );
};

export default Sidebar;
